// Pick API base that avoids cross-origin fetches in production.
// - On custom domains (profit-pulse.pro), Emergent preview URLs, or emergent.host: same-origin
//   /api is routed to the backend by the platform, and mobile browsers / privacy modes don't
//   mistrust the request.
// - On localhost (CRA dev): fall back to REACT_APP_BACKEND_URL so fetches reach the real API pod.
function pickApiBase() {
  if (typeof window === 'undefined' || !window.location) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  const { origin, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || origin;
  }
  return origin;
}
const API_BASE = pickApiBase();

function getToken() {
  return localStorage.getItem('auth_token');
}

async function apiCall(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    const detail = error.detail;
    if (detail && typeof detail === 'object') throw new Error(JSON.stringify(detail));
    throw new Error(detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

export const api = {
  // Auth
  register: (data) => apiCall('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data) => apiCall('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  getMe: () => apiCall('/auth/me'),
  exchangeSession: (sessionId) => apiCall(`/auth/session?session_id=${sessionId}`),
  logout: () => apiCall('/auth/logout', { method: 'POST' }),
  forgotPassword: (email) => apiCall('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (email, code, new_password) => apiCall('/auth/reset-password', { method: 'POST', body: JSON.stringify({ email, code, new_password }) }),
  joinWaitlist: (data) => apiCall('/auth/waitlist', { method: 'POST', body: JSON.stringify(data) }),

  // Notification preferences
  getNotificationPrefs: () => apiCall('/user/notification-preferences'),
  updateNotificationPrefs: (prefs) => apiCall('/user/notification-preferences', { method: 'PUT', body: JSON.stringify(prefs) }),

  // Profile
  updateProfile: (data) => apiCall('/user/profile', { method: 'PUT', body: JSON.stringify(data) }),
  getUserMode: () => apiCall('/user/mode'),
  setUserMode: (mode) => apiCall('/user/mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
  setPreviewMode: (enabled) => apiCall('/user/preview-mode', { method: 'PUT', body: JSON.stringify({ enabled }) }),

  // Platform settings
  getPublicPlatformSettings: () => apiCall('/platform-settings'),
  getAdminPlatformSettings: () => apiCall('/admin/platform-settings'),
  updateAdminPlatformSettings: (settings) => apiCall('/admin/platform-settings', { method: 'PUT', body: JSON.stringify(settings) }),

  // Strategies
  getStrategies: () => apiCall('/strategies'),
  getStrategy: (id) => apiCall(`/strategies/${id}`),
  subscribeStrategy: (id, passcode, activeStudentPasscode) => apiCall(`/strategies/${id}/subscribe`, {
    method: 'POST',
    body: JSON.stringify({ passcode: passcode || null, active_student_passcode: activeStudentPasscode || null }),
  }),
  getUserSubscriptions: () => apiCall('/user/subscriptions'),
  getStrategyEquityCurve: (id, granularity) => apiCall(`/strategies/${id}/equity-curve?granularity=${granularity}`),
  blendStrategies: (data) => apiCall('/strategies/blend', { method: 'POST', body: JSON.stringify(data) }),
  requestQuote: (data) => apiCall(`/strategies/${data.strategy_id}/request-quote`, { method: 'POST', body: JSON.stringify(data) }),
  getActiveStudentStatus: () => apiCall('/user/active-student-status'),
  activateStudentPasscode: (passcode) => apiCall('/user/activate-student', { method: 'POST', body: JSON.stringify({ passcode }) }),
  getAdminSubscriptionRequests: (status) => apiCall(`/admin/subscription-requests${status ? `?status=${status}` : ''}`),
  updateAdminSubscriptionRequest: (id, data) => apiCall(`/admin/subscription-requests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  createActiveStudentPasscode: (userId) => apiCall('/admin/active-student-passcode', { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
  listActiveStudentPasscodes: () => apiCall('/admin/active-student-passcodes'),

  // Admin Strategies
  getAdminStrategies: () => apiCall('/admin/strategies'),
  createAdminStrategy: (data) => apiCall('/admin/strategies', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminStrategy: (id, data) => apiCall(`/admin/strategies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAdminStrategy: (id) => apiCall(`/admin/strategies/${id}`, { method: 'DELETE' }),

  // Market
  getCOT: () => apiCall('/market/cot'),
  getMarketCandles: (symbol, interval, outputsize = 150) => apiCall(`/market/candles?symbol=${symbol}&interval=${interval}&outputsize=${outputsize}`),
  getMarketQuote: (symbol) => apiCall(`/market/quote?symbol=${symbol}`),
  getSentiment: () => apiCall('/market/sentiment'),
  getFearGreed: () => apiCall('/market/fear-greed'),
  getVIX: () => apiCall('/market/vix'),
  getEdgeScore: () => apiCall('/market/edge-score'),
  getMasterPositions: () => apiCall('/market/master-positions'),
  refreshCOT: () => apiCall('/market/cot/refresh'),

  // AI
  aiChat: (data) => apiCall('/ai/chat', { method: 'POST', body: JSON.stringify(data) }),
  getAIHistory: () => apiCall('/ai/history'),

  // Dashboard
  getDashboardOverview: () => apiCall('/dashboard/overview'),
  getStrategyRecommendations: () => apiCall('/dashboard/recommendations'),
  getAccountDetails: (accountId) => apiCall(`/dashboard/account/${accountId}`),
  checkMilestones: () => apiCall('/milestones/check'),
  dismissMilestone: (id) => apiCall(`/milestones/dismiss/${id}`, { method: 'POST' }),
  getUserStats: () => apiCall('/user/stats'),

  // Copy trading
  getFollowerOpenPositions: () => apiCall('/follower/open-positions'),
  getFollowerSubscriptions: (mode = 'simple') => apiCall(`/follower/subscriptions?mode=${mode}`),
  updateCopySettings: (subscriptionId, settings) => apiCall(`/follower/subscriptions/${subscriptionId}/settings`, { method: 'PUT', body: JSON.stringify(settings) }),
  getFollowerPerformance: (mode = 'simple', period = 'all') => apiCall(`/follower/performance?mode=${mode}&period=${period}`),
  getFollowerExecutions: (page = 1, limit = 20) => apiCall(`/follower/executions?page=${page}&limit=${limit}`),
  activateInsurance: (subscriptionId, amount) => apiCall('/insurance/activate', { method: 'POST', body: JSON.stringify({ subscription_id: subscriptionId, amount }) }),
  getInsuranceStatus: (subscriptionId) => apiCall(`/insurance/status/${subscriptionId}`),
  getInsuranceClaims: () => apiCall('/insurance/claims'),
  getAccountsPnl: () => apiCall('/portfolio/accounts-pnl'),
  getAccountCopySettings: () => apiCall('/copy/account-settings'),
  updateAccountCopySettings: (accountId, settings) => apiCall(`/copy/account-settings/${accountId}`, { method: 'PUT', body: JSON.stringify(settings) }),
  // Copy Dashboard
  getLinkedAccountsWithStrategies: () => apiCall('/copy/linked-accounts'),
  getPortfolioEquity: () => apiCall('/copy/portfolio-equity'),
  getClosedTrades: (page = 1, limit = 50, source = null) => apiCall(`/copy/closed-trades?page=${page}&limit=${limit}${source ? `&source=${source}` : ''}`),
  updateAccountStrategySettings: (accountId, strategyId, settings) => apiCall(`/copy/account-strategy-settings/${accountId}/${strategyId}`, { method: 'PUT', body: JSON.stringify(settings) }),
  syncLinkedBrokers: () => apiCall('/copy/sync-linked-brokers', { method: 'POST' }),

  getAccountTradeHistory: (accountId, limit = 20) => apiCall(`/portfolio/trade-history/${accountId}?limit=${limit}`),

  // Brokers
  getBrokers: () => apiCall('/brokers'),
  getBrokerLinks: () => apiCall('/broker-links'),
  linkBroker: (data) => apiCall('/broker-link', { method: 'POST', body: JSON.stringify(data) }),

  // MT5
  connectMT5: (data) => apiCall('/mt5/connect', { method: 'POST', body: JSON.stringify(data) }),
  getMT5Connections: () => apiCall('/mt5/connections'),
  deleteMT5Connection: (id) => apiCall(`/mt5/connections/${id}`, { method: 'DELETE' }),
  searchMT5Brokers: (q) => apiCall(`/mt5/broker-search?q=${encodeURIComponent(q)}`),

  // TradeLocker
  connectTradeLocker: (data) => apiCall('/tradelocker/connect', { method: 'POST', body: JSON.stringify(data) }),
  getTradeLockerConnections: () => apiCall('/tradelocker/connections'),
  deleteTradeLockerConnection: (id) => apiCall(`/tradelocker/connections/${id}`, { method: 'DELETE' }),
  backfillTradeLockerClosedTrades: (id, daysBack = 365) =>
    apiCall(`/tradelocker/connections/${id}/backfill-closed-trades?days_back=${daysBack}`,
            { method: 'POST', body: '{}' }),
  syncClosedTradesNow: (daysBack = 90) =>
    apiCall(`/follower/sync-closed-trades-now?days_back=${daysBack}`,
            { method: 'POST', body: '{}' }),

  // MatchTrader
  connectMatchTrader: (data) => apiCall('/matchtrader/connect', { method: 'POST', body: JSON.stringify(data) }),
  getMatchTraderConnections: () => apiCall('/matchtrader/connections'),
  deleteMatchTraderConnection: (id) => apiCall(`/matchtrader/connections/${id}`, { method: 'DELETE' }),
  verifyMatchTraderBroker: (serverUrl) => apiCall(`/matchtrader/broker-details?server_url=${encodeURIComponent(serverUrl)}`),

  // Bybit (admin-managed)
  adminBybitConnect: (data) => apiCall('/admin/bybit/connect', { method: 'POST', body: JSON.stringify(data) }),
  adminBybitList: (userId) => apiCall(`/admin/bybit/connections${userId ? `?user_id=${encodeURIComponent(userId)}` : ''}`),
  adminBybitDelete: (id) => apiCall(`/admin/bybit/connections/${id}`, { method: 'DELETE' }),
  adminBybitUpdate: (id, data) => apiCall(`/admin/bybit/connections/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  adminBybitSync: (id) => apiCall(`/admin/bybit/sync/${id}`, { method: 'POST', body: '{}' }),
  myBybitConnections: () => apiCall('/bybit/my-connections'),
  // Funded admin
  adminDeleteFundedAccount: (id) => apiCall(`/admin/funded-accounts/${id}`, { method: 'DELETE' }),
  // MetaAPI connection admin (delete = unlink slave broker)
  adminDeleteMetaAPIConnection: (id, deleteFromCloud = false) => apiCall(`/admin/metaapi/connections/${id}${deleteFromCloud ? '?delete_from_cloud=true' : ''}`, { method: 'DELETE' }),
  adminDeleteTradovateConnection: (id) => apiCall(`/admin/tradovate/connections/${id}`, { method: 'DELETE' }),
  adminDeleteRithmicConnection:   (id) => apiCall(`/admin/rithmic/connections/${id}`, { method: 'DELETE' }),
  adminDeleteTradeLockerConnection:(id) => apiCall(`/admin/tradelocker/connections/${id}`, { method: 'DELETE' }),
  adminDeleteMatchTraderConnection:(id) => apiCall(`/admin/matchtrader/connections/${id}`, { method: 'DELETE' }),
  adminDeleteDxTradeConnection:   (id) => apiCall(`/admin/dxtrade/connections/${id}`, { method: 'DELETE' }),
  adminDeleteVolumetricaConnection:(id) => apiCall(`/volumetrica/connections/${id}`, { method: 'DELETE' }),
  adminDeleteBybitConnection:      (id) => apiCall(`/admin/bybit/connections/${id}`, { method: 'DELETE' }),
  adminDeleteCloseOptionConnection:(id) => apiCall(`/closeoption/connections/${id}`, { method: 'DELETE' }),
  // Stealth Compliance Layer
  adminListFirmRules:       ()                => apiCall('/admin/firm-rules'),
  adminUpsertFirmRule:      (firmId, data)    => apiCall(`/admin/firm-rules/${encodeURIComponent(firmId)}`, { method: 'PUT', body: JSON.stringify(data) }),
  userSetFirmFlag:          (firmId, data)    => apiCall(`/firm-rules/${encodeURIComponent(firmId)}/user-flag`, { method: 'PUT', body: JSON.stringify(data) }),
  adminListStealthAccounts: ()                => apiCall('/admin/stealth-accounts'),
  adminCreateStealthAccount:(data)            => apiCall('/admin/stealth-accounts', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdateStealthAccount:(id, data)        => apiCall(`/admin/stealth-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteStealthAccount:(id)              => apiCall(`/admin/stealth-accounts/${id}`, { method: 'DELETE' }),
  adminTestStealthAccount:  (id, data = {})   => apiCall(`/admin/stealth-accounts/${id}/test`, { method: 'POST', body: JSON.stringify(data) }),
  adminCreateStealthTwin:   (brokerType, connId, data = {}) => apiCall(`/admin/broker-accounts/${brokerType}/${connId}/stealth-twin`, { method: 'POST', body: JSON.stringify(data) }),
  adminGetAvailableProxies: ()                => apiCall('/admin/proxies/available'),
  adminListStealthExecutions:(limit = 50)     => apiCall(`/admin/stealth-executions?limit=${limit}`),
  adminBybitProxyGet: () => apiCall('/admin/bybit/proxy-config'),
  adminBybitProxySetGlobal: (data) => apiCall('/admin/bybit/proxy-config/global', { method: 'PUT', body: JSON.stringify(data) }),
  adminBybitProxySetFallback: (data) => apiCall('/admin/bybit/proxy-config/fallback', { method: 'PUT', body: JSON.stringify(data) }),
  adminBybitProxyDelFallback: () => apiCall('/admin/bybit/proxy-config/fallback', { method: 'DELETE' }),
  adminBybitProxyTest: (slot = 'primary', userId = null) => apiCall(`/admin/bybit/proxy-test?slot=${slot}${userId ? `&user_id=${encodeURIComponent(userId)}` : ''}`, { method: 'POST', body: '{}' }),

  // MetaAPI
  connectMetaAPISlaveAccount: (data) => apiCall('/metaapi/slave/connect', { method: 'POST', body: JSON.stringify(data) }),
  getMetaAPISlaveConnections: () => apiCall('/metaapi/slave/connections'),
  getMetaAPISlaveBalances: () => apiCall('/metaapi/slave/balances'),
  refreshMetaAPIStatus: () => apiCall('/metaapi/slave/refresh-status', { method: 'POST', body: '{}' }),
  disconnectMetaAPISlaveAccount: (id) => apiCall(`/metaapi/slave/disconnect/${id}`, { method: 'DELETE' }),
  connectMetaAPIMasterAccount: (data) => apiCall('/admin/metaapi/master/connect', { method: 'POST', body: JSON.stringify(data) }),
  getMetaAPIMasters: () => apiCall('/admin/metaapi/masters'),
  deleteMetaAPIMaster: (id) => apiCall(`/admin/metaapi/masters/${id}`, { method: 'DELETE' }),
  getAllMetaAPIConnections: () => apiCall('/admin/metaapi/all-connections'),

  // Tradovate Playwright
  connectTradovatePW: (data) => apiCall('/tradovate/playwright/connect', { method: 'POST', body: JSON.stringify(data) }),
  getTradovatePWConnections: () => apiCall('/tradovate/playwright/connections'),
  getTradovatePWStatus: () => apiCall('/tradovate/playwright/status'),
  getTradovateConnections: () => apiCall('/tradovate/connections'),
  deleteTradovateConnection: (id) => apiCall(`/tradovate/connections/${id}`, { method: 'DELETE' }),
  getTradovateSymbolMap: () => apiCall('/tradovate/playwright/symbol-map'),
  // Tradovate Multi-Account Discovery (one login → multiple sub-accounts, e.g. APEX_375223, APEX_375224)
  discoverTradovateSubAccounts: (id) => apiCall(`/tradovate/connections/${id}/discover-accounts`, { method: 'POST', body: '{}' }),
  selectTradovateSubAccount: (id, sub) => apiCall(`/tradovate/connections/${id}/select-sub-account`, { method: 'POST', body: JSON.stringify(sub) }),

  // Rithmic
  getRithmicPropFirms: () => apiCall('/rithmic/prop-firms'),
  connectRithmic: (data) => apiCall('/rithmic/connect', { method: 'POST', body: JSON.stringify(data) }),
  getRithmicConnections: () => apiCall('/rithmic/connections'),
  getRithmicStatus: () => apiCall('/rithmic/status'),
  getQuantTowerGuide: () => apiCall('/rithmic/prop-firm-guide'),

  // DXtrade
  connectDXtrade: (data) => apiCall('/dxtrade/connect', { method: 'POST', body: JSON.stringify(data) }),
  getDXtradeConnections: () => apiCall('/dxtrade/connections'),
  getDXtradeStatus: () => apiCall('/dxtrade/status'),

  // Admin
  getAdminFollowers: () => apiCall('/admin/followers'),
  getAdminExecutions: (status) => apiCall(`/admin/executions${status ? `?status=${status}` : ''}`),
  getAdminInsuranceLiability: () => apiCall('/admin/insurance/liability'),
  payInsuranceClaim: (claimId) => apiCall(`/admin/insurance/pay/${claimId}`, { method: 'POST', body: '{}' }),
  getAdminMasterAccounts: () => apiCall('/admin/master-accounts'),
  createAdminMasterAccount: (data) => apiCall('/admin/master-accounts', { method: 'POST', body: JSON.stringify(data) }),
  deleteAdminMasterAccount: (id) => apiCall(`/admin/master-accounts/${id}`, { method: 'DELETE' }),
  getAdminTLConnections: () => apiCall('/admin/tradelocker/connections'),
  getAdminTradovateConnections: () => apiCall('/admin/tradovate/connections'),
  getAdminMatchTraderConnections: () => apiCall('/admin/matchtrader/connections'),
  getAdminQuotes: (status) => apiCall(`/admin/quotes${status ? `?status=${status}` : ''}`),
  updateAdminQuote: (quoteId, updates) => apiCall(`/admin/quotes/${quoteId}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  getAdminWaitlist: (status) => apiCall(`/admin/waitlist${status ? `?status=${status}` : ''}`),
  updateAdminWaitlist: (waitlistId, updates) => apiCall(`/admin/waitlist/${waitlistId}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  deleteAdminWaitlist: (waitlistId) => apiCall(`/admin/waitlist/${waitlistId}`, { method: 'DELETE' }),
  deleteAdminUser: (userId) => apiCall(`/admin/users/${userId}`, { method: 'DELETE' }),
  cleanupTestAccounts: () => apiCall('/admin/users/cleanup-test-accounts', { method: 'POST', body: '{}' }),
  updateAdminUser: (userId, data) => apiCall(`/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  createFundedForUser: (userId, data) => apiCall(`/admin/users/${userId}/funded-account`, { method: 'POST', body: JSON.stringify(data) }),
  adminSubscribeUser: (userId, stratId) => apiCall(`/admin/users/${userId}/subscribe/${stratId}`, { method: 'POST', body: '{}' }),
  adminUnsubscribeUser: (userId, stratId) => apiCall(`/admin/users/${userId}/subscribe/${stratId}`, { method: 'DELETE' }),
  adminUserSubscriptions: (userId) => apiCall(`/admin/users/${userId}/subscriptions`),
  // Manual payment submission (Revolut, wire transfer, etc.)
  submitManualPayment: (data) => apiCall('/payments/manual-submit', { method: 'POST', body: JSON.stringify(data) }),
  getAdminManualPayments: (status) => apiCall(`/admin/manual-payments${status ? `?status=${status}` : ''}`),
  adminApproveManualPayment: (paymentId) => apiCall(`/admin/manual-payments/${paymentId}/approve`, { method: 'POST', body: '{}' }),
  adminRejectManualPayment: (paymentId, reason) => apiCall(`/admin/manual-payments/${paymentId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  revealBrokerPassword: (connId) => apiCall(`/admin/broker-accounts/${connId}/reveal-password`),
  getProxyInventory: () => apiCall('/admin/proxies/available'),
  getPlaywrightHealth: () => apiCall('/admin/playwright/health'),
  getPlaywrightScreenshots: () => apiCall('/admin/playwright/screenshots'),
  testPlaywrightMT5Login: (connId) => apiCall(`/admin/playwright/mt5/test-login/${connId}`, { method: 'POST', body: '{}' }),
  setMT5WebTerminalUrl:   (connId, url) => apiCall(`/admin/playwright/mt5/${connId}/webterminal-url`, { method: 'PATCH', body: JSON.stringify({ webterminal_url: url }) }),
  testPlaywrightMT5Order: (connId, signal) => apiCall(`/admin/playwright/mt5/test-order/${connId}`, { method: 'POST', body: JSON.stringify(signal) }),
  getMetaApiCloudAccounts: () => apiCall('/admin/metaapi/cloud-accounts'),
  autoLinkMetaApiCloudAccounts: () => apiCall('/admin/metaapi/auto-link-cloud-accounts', { method: 'POST', body: '{}' }),
  seedKnownMt5Creds: () => apiCall('/admin/seed-known-mt5-creds', { method: 'POST', body: '{}' }),
  cleanupAndRefreshMetaApi: () => apiCall('/admin/metaapi/cleanup-and-refresh', { method: 'POST', body: '{}' }),
  // Prop-firm PROFILES (per-firm routing rules — proxy/playwright requirements)
  getPropFirmProfiles: () => apiCall('/prop-firm-profiles'),
  getAdminPropFirmProfiles: () => apiCall('/admin/prop-firm-profiles'),
  createAdminPropFirmProfile: (data) => apiCall('/admin/prop-firm-profiles', { method: 'POST', body: JSON.stringify(data) }),
  updateAdminPropFirmProfile: (id, data) => apiCall(`/admin/prop-firm-profiles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteAdminPropFirmProfile: (id) => apiCall(`/admin/prop-firm-profiles/${id}`, { method: 'DELETE' }),
  assignProxyToUser: (data) => apiCall('/admin/proxies/assign', { method: 'POST', body: JSON.stringify(data) }),
  adminInspectRouting: (connId) => apiCall(`/admin/routing/${connId}`),
  unassignUserProxy: (userId) => apiCall(`/admin/proxies/assign/${userId}`, { method: 'DELETE' }),
  getAdminResetRequests: () => apiCall('/admin/password-reset-requests'),
  // Verification (KYC)
  getVerificationStatus: () => apiCall('/verification/status'),
  submitVerification: (data) => apiCall('/verification/submit', { method: 'POST', body: JSON.stringify(data) }),
  startVeriffSession: () => apiCall('/verification/veriff/start', { method: 'POST', body: '{}' }),
  getAdminVerifications: (status) => apiCall(`/admin/verifications${status ? `?status=${status}` : ''}`),
  updateAdminVerification: (userId, data) => apiCall(`/admin/verifications/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Funded Accounts (user)
  getMyFundedAccounts: () => apiCall('/funded-accounts'),
  applyFundedAccount: (data) => apiCall('/funded-accounts/apply', { method: 'POST', body: JSON.stringify(data) }),
  linkMT5ToFundedAccount: (id, data) => apiCall(`/funded-accounts/${id}/link-mt5`, { method: 'POST', body: JSON.stringify(data) }),
  autoLinkFundedAccount: (id) => apiCall(`/funded-accounts/${id}/auto-link`, { method: 'POST', body: '{}' }),
  subscribeFundedAccountToStrategy: (faId, stratId) => apiCall(`/funded-accounts/${faId}/subscribe/${stratId}`, { method: 'POST', body: '{}' }),
  requestPayout: (id, data) => apiCall(`/funded-accounts/${id}/payout`, { method: 'POST', body: JSON.stringify(data) }),

  // Funded Accounts (admin)
  getAdminFundedAccounts: (status) => apiCall(`/admin/funded-accounts${status ? `?status=${status}` : ''}`),
  launchStudentEmails: () => apiCall('/admin/launch-student-emails', { method: 'POST', body: '{}' }),
  launchStudentEmailsForUser: (userId) => apiCall(`/admin/launch-student-emails/${userId}`, { method: 'POST', body: '{}' }),
  getHeldEmails: () => apiCall('/admin/held-emails'),
  getHeldEmailsCount: () => apiCall('/admin/held-emails-count'),
  updateAdminFundedAccount: (id, data) => apiCall(`/admin/funded-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminSubscribeFundedAccount: (faId, stratId) => apiCall(`/admin/funded-accounts/${faId}/subscribe/${stratId}`, { method: 'POST', body: '{}' }),
  adminApprovePayout: (faId, payoutId) => apiCall(`/admin/funded-accounts/${faId}/payout/${payoutId}/approve`, { method: 'POST', body: '{}' }),
  adminDenyPayout: (faId, payoutId, data) => apiCall(`/admin/funded-accounts/${faId}/payout/${payoutId}/deny`, { method: 'POST', body: JSON.stringify(data) }),
  getAdminPayoutDenyReasons: () => apiCall('/admin/payout-deny-reasons'),
  getAdminPayoutRequests: (status) => apiCall(`/admin/payout-requests${status ? `?status=${status}` : ''}`),
  getCopyEquityCurve: (period = '30d', perAccount = false) => apiCall(`/copy/equity-curve?period=${period}${perAccount ? '&per_account=true' : ''}`),
  // Strategy master-infusion controls (Phase A — strategy math)
  toggleMasterInfusion: (strategyId, enabled) => apiCall(`/admin/strategies/${strategyId}/master-infusion`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  runWeeklyInfusion:    () => apiCall('/admin/strategies/run-weekly-infusion', { method: 'POST', body: '{}' }),
  getMasterVsProjection: () => apiCall('/admin/strategies/master-vs-projection'),
  // Native simulated funded accounts (Phase B)
  createNativeFundedAccount: (data) => apiCall('/admin/funded-accounts/native', { method: 'POST', body: JSON.stringify(data) }),
  approveFundedApplication:  (faId, data = {}) => apiCall(`/admin/funded-accounts/${faId}/approve`, { method: 'POST', body: JSON.stringify(data) }),

  // Day-of-week copy schedules
  listCopySchedules:    ()                    => apiCall('/copy/schedules'),
  upsertCopySchedule:   (accountId, data)     => apiCall(`/copy/schedules/${accountId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCopySchedule:   (accountId)           => apiCall(`/copy/schedules/${accountId}`, { method: 'DELETE' }),
  adminListCopySchedules:  (userId)                       => apiCall(`/admin/users/${userId}/copy-schedules`),
  adminUpsertCopySchedule: (userId, accountId, data)      => apiCall(`/admin/users/${userId}/copy-schedules/${accountId}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteCopySchedule: (userId, accountId)            => apiCall(`/admin/users/${userId}/copy-schedules/${accountId}`, { method: 'DELETE' }),
  backfillSimulatedAccount:  (faId, data = {}) => apiCall(`/admin/funded-accounts/${faId}/backfill-simulated`, { method: 'POST', body: JSON.stringify(data) }),
  convertFaToNative:         (faId) => apiCall(`/admin/funded-accounts/${faId}/convert-to-native`, { method: 'POST', body: '{}' }),
  adminBulkConvertToNative:  (body = {}) => apiCall('/admin/funded-accounts/bulk-convert-to-native', { method: 'POST', body: JSON.stringify(body) }),
  adminSetFundedPhase:       (faId, phase) => apiCall(`/admin/funded-accounts/${faId}/phase`, { method: 'PATCH', body: JSON.stringify({ phase }) }),
  adminCloseManualSignal:    (signalId, exitPrice) => apiCall(`/admin/signals/${signalId}/close`, { method: 'POST', body: JSON.stringify(exitPrice !== undefined ? { exit_price: exitPrice } : {}) }),

  // Prop firm rules (admin)
  getAdminPropFirmRules: () => apiCall('/admin/prop-firm-rules'),
  updateAdminPropFirmRules: (data) => apiCall('/admin/prop-firm-rules', { method: 'PUT', body: JSON.stringify(data) }),
  setStrategyPropFirm: (id, data) => apiCall(`/admin/strategies/${id}/prop-firm`, { method: 'PUT', body: JSON.stringify(data) }),
  getPublicPropFirmRules: () => apiCall('/prop-firm-rules'),

  // Admin API Keys
  getAdminApiKeys: () => apiCall('/admin/api-keys'),
  setAdminApiKey: (keyName, value) => apiCall(`/admin/api-keys/${keyName}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  deleteAdminApiKey: (keyName) => apiCall(`/admin/api-keys/${keyName}`, { method: 'DELETE' }),
  getAdminNotifications: (unreadOnly) => apiCall(`/admin/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  markNotificationRead: (id) => apiCall(`/admin/notifications/${id}/read`, { method: 'PUT', body: '{}' }),
  markAllNotificationsRead: () => apiCall('/admin/notifications/read-all', { method: 'PUT', body: '{}' }),
  getProxyQueue: () => apiCall('/admin/tradovate/proxy-queue'),
  getAdminTradeSignals: () => apiCall('/trade/signals'),
  sendTradeSignal: (data) => apiCall('/trade/signal', { method: 'POST', body: JSON.stringify(data) }),

  // ── Chart trading (Batch C) ────────────────────────────────────────────
  getTradeableAccounts:   () => apiCall('/user/tradeable-accounts'),
  getPrimaryAccount:      () => apiCall('/user/primary-account'),
  setPrimaryAccount:      (data) => apiCall('/user/primary-account', { method: 'PUT', body: JSON.stringify(data) }),
  chartPlaceOrder:        (data) => apiCall('/chart/place-order', { method: 'POST', body: JSON.stringify(data) }),
  chartPlacePendingOrder: (data) => apiCall('/chart/place-pending-order', { method: 'POST', body: JSON.stringify(data) }),
  chartPendingOrders:     () => apiCall('/chart/pending-orders'),
  chartCancelPending:     (id) => apiCall(`/chart/pending-orders/${id}`, { method: 'DELETE' }),
  getPersonalStrategy:    () => apiCall('/strategies/me/personal-strategy'),
  // Volumetrica (AquaFutures / AquaFunded)
  volumetricaConnect:     (data) => apiCall('/volumetrica/connect', { method: 'POST', body: JSON.stringify(data) }),
  listVolumetrica:        () => apiCall('/volumetrica/connections'),
  deleteVolumetrica:      (id) => apiCall(`/volumetrica/connections/${id}`, { method: 'DELETE' }),
  // Volumetrica Multi-Account Discovery (one login → multiple eval/funded accounts, e.g. AQ13288, AQ13289)
  discoverVolumetricaSubAccounts: (id) => apiCall(`/volumetrica/connections/${id}/discover-accounts`, { method: 'POST', body: '{}' }),
  selectVolumetricaSubAccount:    (id, sub) => apiCall(`/volumetrica/connections/${id}/select-sub-account`, { method: 'POST', body: JSON.stringify(sub) }),
  // Balance refresh — re-runs the Playwright session and persists fresh equity/balance.
  refreshAccountBalance:          (platform, id) => apiCall(`/portfolio/refresh-balance/${platform}/${id}`, { method: 'POST', body: '{}' }),
  // Closed-trades history scraped from broker portals (Volumetrica today; more to follow)
  fetchVolumetricaClosedTrades:   (id, period = 'week') => apiCall(`/volumetrica/connections/${id}/fetch-closed-trades?period=${period}`, { method: 'POST', body: '{}' }),
  getBrokerClosedTrades:          () => apiCall('/follower/closed-trades').catch(() => ({ trades: [], count: 0 })),

  // CloseOption (binary options — Playwright only, no public API)
  closeoptionConnect:     (data) => apiCall('/closeoption/connect', { method: 'POST', body: JSON.stringify(data) }),
  listCloseOption:        () => apiCall('/closeoption/connections'),
  deleteCloseOption:      (id) => apiCall(`/closeoption/connections/${id}`, { method: 'DELETE' }),
  getCloseOptionWarmPool: () => apiCall('/admin/closeoption/warm-pool'),
  getVolumetricaWarmPool: () => apiCall('/admin/volumetrica/warm-pool'),

  // Admin maintenance
  runRedeployMigrations:  () => apiCall('/admin/run-redeploy-migrations', { method: 'POST', body: '{}' }),
  getSystemHealth:        () => apiCall('/admin/system-health'),
  seedVolumetricaConnection: (data) => apiCall('/admin/seed-broker/volumetrica', { method: 'POST', body: JSON.stringify(data) }),

  // Two-tier access codes + discount codes + quote requests (2026-02)
  getUserAccessStatus:    () => apiCall('/user/access-status'),
  getAllAccessCodes:      () => apiCall('/admin/access-codes'),
  toggleCodePower:        (tier, codeId, enabled) => apiCall(`/admin/access-codes/${tier}/${codeId}/power`, { method: 'PATCH', body: JSON.stringify({ power_enabled: enabled }) }),
  createDiscountCode:     (data) => apiCall('/admin/discount-codes', { method: 'POST', body: JSON.stringify(data) }),
  listDiscountCodes:      () => apiCall('/admin/discount-codes'),
  validateDiscountCode:   (code) => apiCall('/user/discount-codes/validate', { method: 'POST', body: JSON.stringify({ code }) }),
  createQuoteRequest:     (data) => apiCall('/user/quote-requests', { method: 'POST', body: JSON.stringify(data) }),
  listQuoteRequests:      () => apiCall('/admin/quote-requests'),
  closeQuoteRequest:      (id) => apiCall(`/admin/quote-requests/${id}/close`, { method: 'PATCH', body: '{}' }),

  // Password Manager (admin-only — view + reset any user's password)
  listAllPasswords:       () => apiCall('/admin/passwords'),
  adminResetUserPassword: (userId, newPassword, notify = true) => apiCall(`/admin/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify({ new_password: newPassword, notify_user: notify }) }),
  adminVolumetricaTest:   (id) => apiCall(`/admin/volumetrica/${id}/test-connection`, { method: 'POST', body: '{}' }),
  // Strategy ⇄ MetaAPI master linkage
  adminAvailableMetaApiForStrategy: (sid) => apiCall(`/admin/strategies/${sid}/available-metaapi-masters`),
  adminLinkMetaApiAsMaster: (sid, mcId) => apiCall(`/admin/strategies/${sid}/link-metaapi-as-master`, { method: 'POST', body: JSON.stringify({ metaapi_connection_id: mcId }) }),

  getAdminMT5Slaves: () => apiCall('/admin/mt5-slaves'),
  getAdminSlaveErrors: () => apiCall('/admin/slave-errors'),
  getAdminMT5Masters: () => apiCall('/admin/mt5-masters'),
  getAdminMasterSignals: (masterId) => apiCall(`/admin/master-signals${masterId ? `?master_id=${masterId}` : ''}`),

  // Broker Accounts (admin)
  getAdminBrokerAccounts: () => apiCall('/admin/broker-accounts'),
  generatePasscode: (data) => apiCall('/admin/generate-passcode', { method: 'POST', body: JSON.stringify(data) }),
  sendPasscode: (data) => apiCall('/admin/send-passcode', { method: 'POST', body: JSON.stringify(data) }),
  getActiveStudents: () => apiCall('/admin/active-students'),
  createStudent: (data) => apiCall('/admin/create-student', { method: 'POST', body: JSON.stringify(data) }),
  approveWaitlist: (waitlistId) => apiCall(`/admin/waitlist/${waitlistId}/approve`, { method: 'POST' }),
  closeAllTrades: (data) => apiCall('/risk/close-all', { method: 'POST', body: JSON.stringify(data) }),
  setRiskSettings: (accountId, data) => apiCall(`/risk/settings/${accountId}`, { method: 'POST', body: JSON.stringify(data) }),
  getRiskSettings: (accountId) => apiCall(`/risk/settings/${accountId}`),
  resumeCopying: (accountId) => apiCall(`/risk/resume/${accountId}`, { method: 'POST' }),
  setMaster: (data) => apiCall('/broker/metaapi/set-master', { method: 'POST', body: JSON.stringify(data) }),
  unsetMaster: (data) => apiCall('/broker/metaapi/unset-master', { method: 'POST', body: JSON.stringify(data) }),
  createUserStrategy: (data) => apiCall('/strategies/user-create', { method: 'POST', body: JSON.stringify(data) }),
  demoLogin: () => apiCall('/auth/demo-login', { method: 'POST' }),

  // Support Chat
  startSupportChat: (data) => apiCall('/support/chat/start', { method: 'POST', body: JSON.stringify(data || {}) }),
  sendChatMessage: (chatId, content) => apiCall(`/support/chat/${chatId}/message`, { method: 'POST', body: JSON.stringify({ content }) }),
  getChatMessages: (chatId) => apiCall(`/support/chat/${chatId}/messages`),
  getMyChat: () => apiCall('/support/my-chat'),
  closeSupportChat: (chatId) => apiCall(`/support/chat/${chatId}/close`, { method: 'POST' }),
  getAdminSupportChats: () => apiCall('/admin/support-chats'),
  aiSuggestReply: (chatId) => apiCall(`/admin/support-chats/${chatId}/ai-suggest`, { method: 'POST' }),

  // Proxy Management (admin)
  getAvailableProxies: () => apiCall('/admin/proxies/available'),
  assignProxy: (data) => apiCall('/admin/proxies/assign', { method: 'POST', body: JSON.stringify(data) }),
  getProxyAssignments: () => apiCall('/admin/proxies/assignments'),
  removeProxy: (userId) => apiCall(`/admin/proxies/assign/${userId}`, { method: 'DELETE' }),
  getMyProxy: () => apiCall('/user/my-proxy'),

  // Combinations
  createCombination: (data) => apiCall('/combinations', { method: 'POST', body: JSON.stringify(data) }),
  listCombinations: () => apiCall('/combinations'),
  deleteCombination: (id) => apiCall(`/combinations/${id}`, { method: 'DELETE' }),

  // Admin Strategy Approval
  approveStrategy: (strategyId, notes) => apiCall(`/admin/strategies/${strategyId}/approve`, { method: 'POST', body: JSON.stringify({ notes: notes || '' }) }),
  rejectStrategy: (strategyId, notes) => apiCall(`/admin/strategies/${strategyId}/reject`, { method: 'POST', body: JSON.stringify({ notes: notes || '' }) }),
  adminSetPaidStatus: (userId, isPaid) => apiCall(`/admin/users/${userId}/paid-status`, { method: 'PATCH', body: JSON.stringify({ is_paid: isPaid }) }),
  adminListASPasscodes: () => apiCall('/admin/active-student-passcodes'),
  adminBulkGenerateASPasscodes: (count, tag, note) => apiCall('/admin/active-student-passcodes/bulk', { method: 'POST', body: JSON.stringify({ count, tag: tag || '', note: note || '' }) }),
  adminRevokePasscode: (passcodeId) => apiCall(`/admin/active-student-passcodes/${passcodeId}`, { method: 'DELETE' }),
  adminAnnouncementPreview: () => apiCall('/admin/announcements/platform-upgrade/preview'),
  adminAnnouncementSend: (force, temp_password) => apiCall('/admin/announcements/platform-upgrade/send', { method: 'POST', body: JSON.stringify({ force: !!force, temp_password: temp_password || '' }) }),

  // Web Push Notifications
  getVapidKey: () => apiCall('/push/vapid-public-key'),
  getPushTypes: () => apiCall('/push/types'),
  subscribePush: (data) => apiCall('/push/subscribe', { method: 'POST', body: JSON.stringify(data) }),
  unsubscribePush: (data) => apiCall('/push/unsubscribe', { method: 'POST', body: JSON.stringify(data) }),
  getPushPrefs: () => apiCall('/push/preferences'),
  updatePushPrefs: (prefs) => apiCall('/push/preferences', { method: 'PUT', body: JSON.stringify({ prefs }) }),
  pushTest: () => apiCall('/push/test', { method: 'POST' }),

  // Crypto Payments (NOWPayments)
  getPaymentPlans: () => apiCall('/payments/plans'),
  getPaymentCurrencies: () => apiCall('/payments/currencies'),
  createInvoice: (data) => apiCall('/payments/create-invoice', { method: 'POST', body: JSON.stringify(data) }),
  getInvoiceStatus: (invoiceId) => apiCall(`/payments/${invoiceId}/status`),
  adminPayments: () => apiCall('/admin/payments'),
  adminPaymentsStats: () => apiCall('/admin/payments/stats'),

  // Discount Code Requests
  requestDiscount: (data) => apiCall('/discount/request', { method: 'POST', body: JSON.stringify(data) }),
  myDiscountRequests: () => apiCall('/discount/my-requests'),
  validateDiscount: (code) => apiCall('/discount/validate', { method: 'POST', body: JSON.stringify({ code }) }),
  adminDiscountRequests: (status) => apiCall(`/admin/discount-requests${status ? `?status=${status}` : ''}`),
  adminApproveDiscount: (rid, percent, message) => apiCall(`/admin/discount-requests/${rid}/approve`, { method: 'POST', body: JSON.stringify({ percent, message: message || '' }) }),
  adminDenyDiscount: (rid, message) => apiCall(`/admin/discount-requests/${rid}/deny`, { method: 'POST', body: JSON.stringify({ message: message || '' }) }),

  // Funded AS Requests
  fundedAsRequest: (data) => apiCall('/funded/as-request', { method: 'POST', body: JSON.stringify(data) }),
  myFundedAsRequests: () => apiCall('/funded/my-as-requests'),
  adminFundedAsRequests: (status) => apiCall(`/admin/funded-as-requests${status ? `?status=${status}` : ''}`),
  adminApproveFundedPaid: (rid, message) => apiCall(`/admin/funded-as-requests/${rid}/approve-paid`, { method: 'POST', body: JSON.stringify({ message: message || '' }) }),
  adminApproveFundedCharge: (rid, amount_usd, message) => apiCall(`/admin/funded-as-requests/${rid}/approve-charge`, { method: 'POST', body: JSON.stringify({ amount_usd, message: message || '' }) }),
  adminDenyFundedAs: (rid, message) => apiCall(`/admin/funded-as-requests/${rid}/deny`, { method: 'POST', body: JSON.stringify({ message: message || '' }) }),

  // Custom Charge (admin-generated payment link)
  getCharge: (chargeId) => apiCall(`/payments/charge/${chargeId}`),
  createInvoiceForCharge: (chargeId, pay_currency) => apiCall(`/payments/charge/${chargeId}/create-invoice`, { method: 'POST', body: JSON.stringify({ pay_currency }) }),
};

export default api;
